import { useEffect } from 'react';
import cl from 'classnames';

import { ArchivedFile } from '../../../../DownloadUrlsModalButton.types';
import { FileAttachmentDeepArchiveRestoreRequest } from '../../../../../../../../deepArchiveRestoreRequests/queries/fileAttachmentDeepArchiveRestoreRequests.query';

import { FETCH_FILE_ATTACHMENT_DEEP_ARCHIVE_RESTORE_REQUESTS_QUERY } from '../../../../../../../../deepArchiveRestoreRequests/queries/fileAttachmentDeepArchiveRestoreRequests.query';

import { useDeepArchiveRestoreRequests } from '../../../../../../../../deepArchiveRestoreRequests/hooks/useDeepArchiveRestoreRequests';

import { IndexTableCheckbox } from '../../../../../../tables/IndexTableCheckbox';

import { AlertMessage } from '../../../../../../../../../helpers/AlertMessage';
import { Icon } from '../../../../../../../../../helpers/Icon';
import { Loading } from '../../../../../../../../../helpers/Loading';
import { DateHelper } from '../../../../../../../../../helpers/DateHelper';
import { Translate } from '../../../../../../../../../helpers/Translate';

import { DeepArchiveRestoreRequestCache } from '../../../../../../../../deepArchiveRestoreRequests/deepArchiveRestoreRequestCache';
import { DeepArchiveRestoreRequestStatusesEnum } from '../../../../../../../../deepArchiveRestoreRequests/deepArchiveRestoreRequestsTypes';
import { stringsKeys } from '../../../../../../../../../locales/keys';

import { getFileIcon } from '../../../../../../../../../utils/getFileIcon';
import { getFileNameFromURL } from '../../../../../../../../../utils/getFileNameFromURL';
import { getCustomDateFormat } from '../../../../../../../../../utils/getCustomDateFormat';

interface ArchivedFilesListItemProps {
  archivedFile: ArchivedFile;
  checked: boolean;
  disabled: boolean;
  onCheck: (fileId: ArchivedFile['id']) => void;
  onSelectableChange: (fileId: ArchivedFile['id'], selectable: boolean) => void;
}

function ArchivedFilesListItem({
  archivedFile,
  checked,
  disabled,
  onCheck,
  onSelectableChange
}: ArchivedFilesListItemProps) {
  const {
    deepArchiveRestoreRequests,
    deepArchiveRestoreRequestsError,
    deepArchiveRestoreRequestsFetched
  } = useDeepArchiveRestoreRequests<FileAttachmentDeepArchiveRestoreRequest>({
    cacheKey:
      DeepArchiveRestoreRequestCache.fileAttachmentDeepArchiveRestoreRequestsCacheKey(
        archivedFile.id
      ),
    query: FETCH_FILE_ATTACHMENT_DEEP_ARCHIVE_RESTORE_REQUESTS_QUERY,
    initialFilters: { fileAttachmentId: archivedFile.id },
    initialLimit: 1,
    options: {
      cacheTime: 0,
      enabled: true,
      enabledPlaceholder: false,
      withoutPrefetch: true
    }
  });

  const restoreRequest = deepArchiveRestoreRequests?.[0];
  const isRestoreRequestValid =
    !!restoreRequest &&
    (restoreRequest.status === DeepArchiveRestoreRequestStatusesEnum.PENDING ||
      restoreRequest.status ===
        DeepArchiveRestoreRequestStatusesEnum.IN_PROGRESS) &&
    !!restoreRequest.estimatedReadyAt &&
    new Date(restoreRequest.estimatedReadyAt).getTime() >= new Date().getTime();
  const selectable =
    deepArchiveRestoreRequestsFetched &&
    !deepArchiveRestoreRequestsError &&
    !isRestoreRequestValid;

  useEffect(() => {
    onSelectableChange(archivedFile.id, selectable);

    return () => onSelectableChange(archivedFile.id, false);
  }, [archivedFile.id, onSelectableChange, selectable]);

  return (
    <div
      className={cl('file-list-item flex-col', {
        selected: selectable && checked
      })}
    >
      <div className="flex items-center flex-1 space-x-2 2xl:space-x-4 max-w-full">
        <IndexTableCheckbox
          checkBoxClassName="basic-checkbox"
          checked={selectable && checked}
          disabled={!selectable || disabled}
          className="flex items-center"
          itemId={archivedFile.id}
          onCheck={onCheck}
          scope="archived-file"
        />

        <div className="flex flex-1 relative h-6">
          <div className="absolute inset-x-0 -inset-y-1 2xl:-inset-y-1.5 flex items-center gap-2">
            <Icon
              icon={getFileIcon(archivedFile.url)}
              className="h-5 w-5 mx-0.5"
            />

            <div className="flex-1 flex truncate leading-6">
              <span className="truncate">
                {getFileNameFromURL(archivedFile.url)}
              </span>
            </div>
            {!deepArchiveRestoreRequestsFetched && <Loading size="2xs" />}
          </div>
        </div>
      </div>
      {isRestoreRequestValid && (
        <div className="pl-7 pt-1 text-sm text-gray-500 dark:text-gray-400">
          <Translate
            id={
              stringsKeys.restorationRequestAlreadySentEstimatedRestorationDate
            }
          />{' '}
          {restoreRequest?.estimatedReadyAt ? (
            <DateHelper
              date={restoreRequest.estimatedReadyAt}
              customFormat={getCustomDateFormat(
                restoreRequest.estimatedReadyAt,
                true
              )}
            />
          ) : (
            '—'
          )}
        </div>
      )}
      <AlertMessage message={deepArchiveRestoreRequestsError} />
    </div>
  );
}

export default ArchivedFilesListItem;
